import type { ColumnType } from "antd/es/table";

// Função auxiliar para extrair texto de um valor que pode ser ReactNode
function extractTextFromRender(rendered) {
	if (typeof rendered === "string" || typeof rendered === "number") {
		return rendered;
	}
	if (rendered?.props?.children) {
		return extractTextFromRender(rendered.props.children);
	}
	if (Array.isArray(rendered)) {
		return rendered.map(extractTextFromRender).join(" ");
	}
	return "";
}

function downloadCSV(content: string, filename = "relatorio.csv") {
	const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

// biome-ignore lint/suspicious/noExplicitAny: dynamic
function getOneRowCSV(columns: any, item: any): string {
	return columns
		.map((col) => {
			const { dataIndex, render } = col;

			// Pega valor aninhado de forma segura
			const value = Array.isArray(dataIndex) ? dataIndex.reduce((obj, key) => (obj ? obj[key] : ""), item) : item?.[dataIndex];

			// Se tiver render customizado, aplica
			if (typeof render === "function") {
				try {
					// render pode retornar ReactNode, então tratamos como texto
					const rendered = render(value, item);
					return extractTextFromRender(rendered);
				} catch {
					return "";
				}
			}

			return value ?? "";
		})
		.join(";");
}

export type CsvData<T = unknown> = {
	columns: ColumnType<T>[];
	data: T[];
};

/**
 * Exporta registros para CSV, com base em colunas do Ant Design.
 * @param {Array} data - Lista de objetos retornados da API.
 * @param {Array} columns - Definição de colunas do Ant Design.
 * @param {string} fileName - Nome do arquivo CSV.
 */
export function exportCSVFromAntdTable<T = unknown>({ data, columns }: CsvData<T>): string {
	if (!Array.isArray(data)) {
		return "";
	}

	if (!Array.isArray(columns)) {
		return "";
	}

	const headerRow = columns.map((col) => col.title).join(";");

	const rows = data.map((item) => {
		return getOneRowCSV(columns, item);
	});

	const csvContent = [headerRow, ...rows].join("\n");

	return csvContent;
}

export type CsvDataMultiTable<T = unknown> = CsvData<T> & {
	parentColumn: string;
	childKey: string;
};

export function exportCSVMultiTable<T = unknown>({ parentColumn, childKey, data, columns }: CsvDataMultiTable<T>): string {
	let csvContent = "";

	for (const parent of data) {
		const par = parent as Record<string, unknown>;
		csvContent += `${par[parentColumn]}\n`;

		const childrenArray = Array.isArray(par[childKey]) ? (par[childKey] as T[]) : [];

		csvContent += `${exportCSVFromAntdTable<T>({ columns, data: childrenArray })}\n\n`; // espaço entre tabelas
	}

	return csvContent;
}

export type CsvDataExpandable<TParent = unknown, TChild = unknown> = {
	parentColumns: ColumnType<TParent>[];
	childColumns: ColumnType<TChild>[];
	childKey: string;
	data: TParent[];
};

export function exportCSVExpandableTable<TParent = unknown, TChild = unknown>({
	childKey,
	parentColumns,
	childColumns,
	data,
}: CsvDataExpandable<TParent, TChild>): string {
	let csvContent = "";

	for (const parent of data) {
		const par = parent as Record<string, unknown>;
		csvContent += `${getOneRowCSV(parentColumns, parent)}\n`;

		const childrenArray = Array.isArray(par[childKey]) ? (par[childKey] as TChild[]) : [];

		csvContent += `${exportCSVFromAntdTable<TChild>({ columns: childColumns, data: childrenArray })}\n`;
	}

	return csvContent;
}

export type CsvType = "default" | "multitable" | "expandable";
export type CsvDataType<T = unknown, TChild = unknown> = CsvData<T> | CsvDataMultiTable<T> | CsvDataExpandable<T, TChild>;
export type TExportToCSV<T = unknown, TChild = unknown> = {
	type: CsvType;
	options: CsvDataType<T, TChild>;
	fileName: string;
};

export function exportToCSV<T = unknown, TChild = unknown>({ type, options, fileName }: TExportToCSV<T, TChild>): void {
	let csvContent = "";

	switch (type) {
		case "multitable":
			csvContent = exportCSVMultiTable<T>(options as CsvDataMultiTable<T>);
			break;
		case "expandable":
			csvContent = exportCSVExpandableTable<T, TChild>(options as CsvDataExpandable<T, TChild>);
			break;
		default:
			csvContent = exportCSVFromAntdTable<T>(options as CsvData<T>); // ou outra implementação padrão
			break;
	}

	downloadCSV(csvContent, fileName);
}

function getCellValue<T>(col: ColumnType<T>, item: T): string | number {
	const { dataIndex, render } = col;
	const value = Array.isArray(dataIndex)
		? dataIndex.reduce((obj: unknown, key) => (obj ? (obj as Record<string, unknown>)[String(key)] : ""), item)
		: item?.[dataIndex as keyof T];

	if (typeof render === "function") {
		try {
			const rendered = render(value as never, item, 0);
			return extractTextFromRender(rendered);
		} catch {
			return "";
		}
	}

	if (typeof value === "number") {
		return value;
	}

	return value == null ? "" : String(value);
}

export async function exportToXLSX<T = unknown, TChild = unknown>({ type, options, fileName }: TExportToCSV<T, TChild>): Promise<void> {
	const ExcelJS = (await import("exceljs")).default;
	const { saveAs } = await import("file-saver");
	const workbook = new ExcelJS.Workbook();
	const sheet = workbook.addWorksheet("Relatório");

	const addTable = <TRow,>(columns: ColumnType<TRow>[], rows: TRow[]) => {
		sheet.addRow(columns.map((col) => String(col.title ?? "")));
		for (const row of rows) {
			sheet.addRow(columns.map((col) => getCellValue(col, row)));
		}
	};

	switch (type) {
		case "multitable": {
			const multi = options as CsvDataMultiTable<T>;
			for (const parent of multi.data) {
				const par = parent as Record<string, unknown>;
				sheet.addRow([String(par[multi.parentColumn] ?? "")]);
				const children = Array.isArray(par[multi.childKey]) ? (par[multi.childKey] as T[]) : [];
				addTable(multi.columns, children);
				sheet.addRow([]);
			}
			break;
		}
		case "expandable": {
			const expandable = options as CsvDataExpandable<T, TChild>;
			for (const parent of expandable.data) {
				sheet.addRow(expandable.parentColumns.map((col) => getCellValue(col, parent)));
				const par = parent as Record<string, unknown>;
				const children = Array.isArray(par[expandable.childKey]) ? (par[expandable.childKey] as TChild[]) : [];
				addTable(expandable.childColumns, children);
				sheet.addRow([]);
			}
			break;
		}
		default: {
			const simple = options as CsvData<T>;
			addTable(simple.columns, simple.data);
			break;
		}
	}

	sheet.columns.forEach((column) => {
		column.width = 18;
	});

	const buffer = await workbook.xlsx.writeBuffer();
	saveAs(
		new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
		`${fileName}.xlsx`,
	);
}
